import { LitElement, html, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import { SVG_CLOSE, SVG_CHEVRON_LEFT, SVG_CHEVRON_RIGHT, SVG_DOWNLOAD } from '@getgo/chameleon-icons'
import { GalleryEvent, type PreviewLoadErrorEventDetail } from '@/models/gallery-event'
import { contentTypeToSvg } from '@/utils/content-type-svg'
import { contentTypeToLabel } from '@/utils/content-type-label'
import { t } from '@/i18n/translate-utils'
import type { FileModel } from '@/models/gallery.model'
import styles from './gallery-details-view.styles.scss?inline'

/**
 * Full-page detail view for a single file in the GoTo Gallery SPA.
 *
 * Shows a file preview (image or icon fallback), navigation controls to move
 * between files, a select checkbox, and a download button.
 *
 * Keyboard shortcuts:
 * - `Escape` emits `detail-close`.
 * - `ArrowLeft` emits `detail-prev`.
 * - `ArrowRight` emits `detail-next`.
 *
 * @prop file          - The file to display.
 * @prop index         - Zero-based index of the current file.
 * @prop total         - Total number of files in the gallery.
 * @prop isSelected    - Whether the current file is selected.
 * @prop selectedCount - Number of currently selected files.
 * @prop selectedSize  - Formatted total size of selected files.
 * @prop isDownloading - True while a download is in progress.
 *
 * @fires detail-close    - `CustomEvent<void>` when the close button is clicked or `Escape` is pressed.
 * @fires detail-prev     - `CustomEvent<void>` when the previous button is clicked or `ArrowLeft` is pressed.
 * @fires detail-next     - `CustomEvent<void>` when the next button is clicked or `ArrowRight` is pressed.
 * @fires file-select     - `CustomEvent<string>` with the file ID when the select checkbox changes.
 * @fires download-request - `CustomEvent<void>` when the download button is clicked.
 */
export class GalleryDetailsView extends LitElement {
  static readonly tagName = 'gallery-details-view'
  static styles = unsafeCSS(styles)

  /** The file to display. If null the component renders nothing. */
  @property({ attribute: false }) file: FileModel | null = null

  /** Zero-based index of the current file in the gallery. */
  @property({ type: Number }) index = 0

  /** Total number of files in the gallery. */
  @property({ type: Number }) total = 0

  /** Whether the current file is included in the selected set. */
  @property({ type: Boolean }) isSelected = false

  /** Number of currently selected files (drives the download button label and enabled state). */
  @property({ type: Number }) selectedCount = 0

  /** Formatted total size of all selected files (e.g. "4.2 MB"). */
  @property({ type: String }) selectedSize = ''

  /** True while a ZIP download is in progress; disables the download button. */
  @property({ type: Boolean }) isDownloading = false

  override connectedCallback(): void {
    super.connectedCallback()
    document.addEventListener('keydown', this._handleKeydown, true)
  }

  override disconnectedCallback(): void {
    document.removeEventListener('keydown', this._handleKeydown, true)
    super.disconnectedCallback()
  }

  private _handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this._onClose()
    } else if (event.key === 'ArrowLeft') {
      this._onPrev()
    } else if (event.key === 'ArrowRight') {
      this._onNext()
    }
  }

  /** Emits `detail-close`. */
  private _onClose(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.DETAIL_CLOSE, { bubbles: true, composed: true }))
  }

  /** Emits `detail-prev`. */
  private _onPrev(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.DETAIL_PREV, { bubbles: true, composed: true }))
  }

  /** Emits `detail-next`. */
  private _onNext(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.DETAIL_NEXT, { bubbles: true, composed: true }))
  }

  /** Emits `file-select` with the current file ID. */
  private _onSelect(): void {
    if (!this.file) {
      return
    }
    this.dispatchEvent(
      new CustomEvent<string>(GalleryEvent.FILE_SELECT, {
        detail: this.file.id,
        bubbles: true,
        composed: true,
      }),
    )
  }

  /** Emits `download-request`. */
  private _onDownload(): void {
    if (this.selectedCount === 0 || this.isDownloading) {
      return
    }

    this.dispatchEvent(new CustomEvent(GalleryEvent.DOWNLOAD_REQUEST, { bubbles: true, composed: true }))
  }

  /** Emits `preview-load-error` with the current file ID. */
  private _onPreviewLoadError(): void {
    if (!this.file) {
      return
    }

    this.dispatchEvent(
      new CustomEvent<PreviewLoadErrorEventDetail>(GalleryEvent.PREVIEW_LOAD_ERROR, {
        detail: { id: this.file.id },
        bubbles: true,
        composed: true,
      }),
    )
  }

  override render() {
    if (!this.file) {
      return html``
    }

    const isPrevDisabled = this.index === 0
    const isNextDisabled = this.index >= this.total - 1
    const downloadDisabled = this.selectedCount === 0 || this.isDownloading

    const preview = this.file.thumbnailUrl
      ? html`<img
          class="preview-img"
          src="${this.file.thumbnailUrl}"
          alt="${this.file.name}"
          @error=${this._onPreviewLoadError}
        />`
      : html`<div class="preview-icon-wrap">
          <chameleon-svg class="preview-icon">${unsafeSVG(contentTypeToSvg(this.file.contentType))}</chameleon-svg>
        </div>`

    return html`
      <div class="details-header">
        <div class="header-info">
          <h2 class="header-title">${this.file.name}</h2>
          <span class="header-type">${contentTypeToLabel(this.file.contentType)}</span>
        </div>
        <chameleon-icon-button
          id="close-button"
          size="large"
          variant="neutral-tertiary"
          aria-label="${t('gallery.actions.close', 'Close')}"
          @click=${this._onClose}
        >
          <chameleon-svg>${unsafeSVG(SVG_CLOSE)}</chameleon-svg>
        </chameleon-icon-button>
        <chameleon-tooltip trigger-id="close-button"> ${t('gallery.actions.close', 'Close')} </chameleon-tooltip>
      </div>

      <div class="select-bar">
        <chameleon-checkbox .checked=${this.isSelected} @change=${this._onSelect}
          >${t('gallery.detail.select', 'Select')}</chameleon-checkbox
        >
      </div>

      <div class="preview-area">${preview}</div>

      <div class="nav-bar">
        <chameleon-icon-button
          size="medium"
          variant="neutral-tertiary"
          ?disabled=${isPrevDisabled}
          aria-label="${t('gallery.navigation.previousFile', 'Previous file')}"
          @click=${this._onPrev}
        >
          <chameleon-svg>${unsafeSVG(SVG_CHEVRON_LEFT)}</chameleon-svg>
        </chameleon-icon-button>
        <span class="nav-counter"
          >${t('gallery.detail.fileCount', '{{index}} of {{total}} files', {
            index: this.index + 1,
            total: this.total,
          })}</span
        >
        <chameleon-icon-button
          size="medium"
          variant="neutral-tertiary"
          ?disabled=${isNextDisabled}
          aria-label="${t('gallery.navigation.nextFile', 'Next file')}"
          @click=${this._onNext}
        >
          <chameleon-svg>${unsafeSVG(SVG_CHEVRON_RIGHT)}</chameleon-svg>
        </chameleon-icon-button>
      </div>

      <div class="bottom-bar">
        <chameleon-button
          variant="brand-primary"
          size="large"
          ?is-loading=${this.isDownloading}
          ?disabled=${downloadDisabled}
          @click=${this._onDownload}
        >
          <chameleon-svg slot="start" style="--goto-icon-size: 20px">${unsafeSVG(SVG_DOWNLOAD)}</chameleon-svg>
          ${t('gallery.actions.download', 'Download files ({{count}})', {
            count: this.selectedCount,
          })}
        </chameleon-button>
      </div>
    `
  }
}
